// 档2 — 豆包端到端实时语音大模型 3.0 playground。与 OpenAI Realtime 共用同一套沉浸式云球 UI/
// 交互模式，但驱动豆包二进制协议(而非 JSON 事件)。
//
// Flow: getWsToken → 连 ws-gateway(桥到 wss://openspeech.bytedance.com/api/v3/realtime/dialogue)
//   → StartConnection → ConnectionStarted(50) → StartSession(100,配置) → SessionStarted(150)
//   → 麦克风 PCM16/16k → TaskRequest(200) 上行；下行 TTSResponse(352,PCM24k) 播放 + ChatResponse(550)
//   文本字幕 + ASRResponse(451) 用户转写 + Usage(154) token 用量。断开时 FinishSession→FinishConnection。

import React from 'react';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import WsRealtimeOrb from './ws-realtime-orb.jsx';
import { DoubaoRealtimeClient, DoubaoServerEvent as SE } from './ws-doubao-client.js';

const RATE_IN = 16000; // 上行 PCM16 mono
const RATE_OUT = 24000; // 下行 PCM Float32 mono(StartSession 配置 tts=pcm/24000)

const PHASE_LABEL = {
  idle: '未连接',
  connecting: '连接中…',
  ready: '准备就绪 · 点麦克风开始说话',
  listening: '聆听中…',
  thinking: '思考中…',
  speaking: '说话中…',
};

// Float32 @inputRate → Int16 @RATE_IN(线性抽取)
function floatToPcm16(float32, inputRate) {
  const ratio = inputRate / RATE_IN;
  const outLen = Math.floor(float32.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const s = Math.max(-1, Math.min(1, float32[Math.floor(i * ratio)]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return new Uint8Array(out.buffer);
}
const isSecure = () =>
  typeof window !== 'undefined' &&
  (window.isSecureContext || /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname));

const priceOf = (wsBilling) => ({
  inText: Number(wsBilling.inputTextTokenUsd ?? wsBilling.inputTokenUsd) || 0,
  inAudio: Number(wsBilling.inputAudioTokenUsd ?? wsBilling.inputTokenUsd) || 0,
  inCached: Number(wsBilling.inputCachedTokenUsd ?? wsBilling.inputTokenUsd) || 0,
  outText: Number(wsBilling.outputTextTokenUsd ?? wsBilling.outputTokenUsd) || 0,
  outAudio: Number(wsBilling.outputAudioTokenUsd ?? wsBilling.outputTokenUsd) || 0,
});

export default function WsPlaygroundDoubao({ api, endpoint }) {
  const wsConfig = (endpoint && endpoint._raw && endpoint._raw.wsConfig) || {};
  const wsBilling = (endpoint && endpoint._raw && endpoint._raw.wsBilling) || {};
  const clientPath = wsConfig.clientPath || '/v1/realtime';
  const price = React.useMemo(() => priceOf(wsBilling), [wsBilling]);

  const [status, setStatus] = React.useState('idle'); // idle|connecting|ready|recording|closed|error
  const [turns, setTurns] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [usage, setUsage] = React.useState({ inTok: 0, outTok: 0, outAudio: 0 });
  const [errorMsg, setErrorMsg] = React.useState('');
  const [modality, setModality] = React.useState('audio'); // text|audio(默认语音 → 沉浸式云球)
  const [botName, setBotName] = React.useState('豆包');
  const [systemRole, setSystemRole] = React.useState('');
  const [speakingStyle, setSpeakingStyle] = React.useState('');
  const [showCfg, setShowCfg] = React.useState(true); // 默认展开，让人设 system_role 一进来就可见
  const [phase, setPhase] = React.useState('idle');

  const clientRef = React.useRef(new DoubaoRealtimeClient());
  const wsRef = React.useRef(null);
  const statusRef = React.useRef('idle');
  const sidRef = React.useRef(null);
  const asstRef = React.useRef(null);
  const idRef = React.useRef(0);
  const cfgRef = React.useRef({});
  const chainRef = React.useRef(Promise.resolve()); // 帧顺序串行处理
  const finishedRef = React.useRef(false);
  // 音频
  const micCtxRef = React.useRef(null);
  const micStreamRef = React.useRef(null);
  const micNodeRef = React.useRef(null);
  const playCtxRef = React.useRef(null);
  const playAtRef = React.useRef(0);
  const playSourcesRef = React.useRef([]);
  const micAnalyserRef = React.useRef(null);
  const outAnalyserRef = React.useRef(null);

  const setStatusBoth = (s) => { statusRef.current = s; setStatus(s); };
  const nextId = () => `t${idRef.current++}`;

  const teardownAudio = React.useCallback(() => {
    if (micNodeRef.current) { try { micNodeRef.current.disconnect(); } catch {} micNodeRef.current = null; }
    if (micCtxRef.current) { try { micCtxRef.current.close(); } catch {} micCtxRef.current = null; }
    if (micStreamRef.current) { try { micStreamRef.current.getTracks().forEach((t) => t.stop()); } catch {} micStreamRef.current = null; }
    if (playCtxRef.current) { try { playCtxRef.current.close(); } catch {} playCtxRef.current = null; }
    micAnalyserRef.current = null; outAnalyserRef.current = null;
    playAtRef.current = 0; playSourcesRef.current = [];
  }, []);

  const gracefulFinish = React.useCallback(() => {
    // 豆包必须 FinishSession → FinishConnection 收尾，否则上游报 55000001。
    const ws = wsRef.current, sid = sidRef.current, c = clientRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && sid && !finishedRef.current) {
      finishedRef.current = true;
      try { ws.send(c.finishSession(sid)); ws.send(c.finishConnection()); } catch {}
    }
  }, []);

  const teardown = React.useCallback((closeSocket = true) => {
    teardownAudio();
    if (closeSocket && wsRef.current) {
      gracefulFinish();
      try { wsRef.current.close(1000, 'client stop'); } catch {}
      wsRef.current = null;
    }
  }, [teardownAudio, gracefulFinish]);

  React.useEffect(() => () => teardown(true), [teardown]);

  const sendFrame = React.useCallback((buf) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && buf) ws.send(buf);
  }, []);

  // 播放一段 Float32 LE 24k PCM(无缝排队)
  const playAudio = React.useCallback((bytes) => {
    try {
      if (!bytes || bytes.byteLength < 4) return;
      if (!playCtxRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        playCtxRef.current = new AudioCtx();
        playAtRef.current = playCtxRef.current.currentTime;
        const an = playCtxRef.current.createAnalyser();
        an.fftSize = 1024; an.connect(playCtxRef.current.destination);
        outAnalyserRef.current = an;
      }
      const ctx = playCtxRef.current;
      const outBus = outAnalyserRef.current || ctx.destination;
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      const n = Math.floor(bytes.byteLength / 4);
      if (n === 0) return;
      const buf = ctx.createBuffer(1, n, RATE_OUT);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < n; i++) ch[i] = Math.max(-1, Math.min(1, view.getFloat32(i * 4, true)));
      const src = ctx.createBufferSource();
      src.buffer = buf; src.connect(outBus);
      src.onended = () => { playSourcesRef.current = playSourcesRef.current.filter((s) => s !== src); };
      playSourcesRef.current.push(src);
      const at = Math.max(playAtRef.current, ctx.currentTime);
      src.start(at); playAtRef.current = at + buf.duration;
    } catch { /* 忽略坏帧 */ }
  }, []);

  const stopPlayback = React.useCallback(() => {
    for (const s of playSourcesRef.current) { try { s.stop(); } catch {} }
    playSourcesRef.current = [];
    if (playCtxRef.current) playAtRef.current = playCtxRef.current.currentTime;
  }, []);

  const appendAssistant = React.useCallback((delta) => {
    if (!delta) return;
    setTurns((prev) => {
      let id = asstRef.current;
      if (!id) { id = nextId(); asstRef.current = id; return [...prev, { id, role: 'assistant', text: delta, streaming: true }]; }
      return prev.map((t) => (t.id === id ? { ...t, text: t.text + delta } : t));
    });
  }, []);

  const setUserCaption = React.useCallback((text, done) => {
    if (!text) return;
    setTurns((prev) => {
      const lastUser = [...prev].reverse().find((t) => t.role === 'user' && t.streaming);
      if (lastUser) return prev.map((t) => (t.id === lastUser.id ? { ...t, text, streaming: !done } : t));
      return [...prev, { id: nextId(), role: 'user', text, streaming: !done }];
    });
  }, []);

  const handleFrame = React.useCallback((f) => {
    if (!f || f.event == null) {
      if (f && f.msgType === 15) setErrorMsg(`上游错误 code=${f.code || ''} ${(f.payload && f.payload.error) || ''}`);
      return;
    }
    switch (f.event) {
      case SE.ConnectionStarted: { // 50 → 建会话
        sidRef.current = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `sess-${Date.now()}`;
        sendFrame(clientRef.current.startSession(sidRef.current, cfgRef.current));
        break;
      }
      case SE.SessionStarted: // 150 → 就绪 → 自动开麦（像通话，连接即开始说话）
        setStatusBoth('ready'); setPhase('ready');
        startMic();
        break;
      case SE.ASRResponse: { // 451 用户转写
        const results = f.payload && f.payload.results;
        const text = Array.isArray(results) ? results.map((r) => (r && r.text) || '').join('') : '';
        if (text) { setUserCaption(text, false); setPhase('listening'); }
        break;
      }
      case SE.ChatResponse: // 550 模型回复文本
        appendAssistant((f.payload && f.payload.content) || '');
        setPhase('speaking');
        break;
      case SE.TTSResponse: // 352 下行音频
        if (f.audio) { playAudio(f.audio); setPhase('speaking'); }
        break;
      case SE.TTSEnded: { // 359 一轮结束
        const id = asstRef.current; asstRef.current = null;
        if (id) setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, streaming: false } : t)));
        setTurns((prev) => prev.map((t) => (t.role === 'user' && t.streaming ? { ...t, streaming: false } : t)));
        setPhase(statusRef.current === 'recording' ? 'listening' : 'ready');
        break;
      }
      case SE.Usage: { // 154 用量
        const u = DoubaoRealtimeClient.extractUsage(f.payload);
        setUsage((p) => ({
          inTok: p.inTok + u.inText + u.inAudio + u.cached,
          outTok: p.outTok + u.outText + u.outAudio,
          outAudio: p.outAudio + u.outAudio,
        }));
        break;
      }
      case SE.SessionFailed: // 153
      case SE.ConnectionFailed: // 51
        setErrorMsg((f.payload && (f.payload.error || JSON.stringify(f.payload))) || `事件 ${f.event} 失败`);
        break;
      default: break;
    }
  }, [appendAssistant, playAudio, sendFrame, setUserCaption]);

  const connect = async () => {
    setErrorMsg(''); setTurns([]); setUsage({ inTok: 0, outTok: 0, outAudio: 0 });
    asstRef.current = null; sidRef.current = null; finishedRef.current = false;
    // StartSession 配置(与 OpenAI session.update 对位)：tts 输出 PCM24k + dialog 人设。
    cfgRef.current = {
      tts: { audio_config: { channel: 1, format: 'pcm', sample_rate: RATE_OUT } },
      dialog: {
        bot_name: botName || '豆包',
        ...(systemRole ? { system_role: systemRole } : {}),
        ...(speakingStyle ? { speaking_style: speakingStyle } : {}),
      },
    };
    setStatusBoth('connecting'); setPhase('connecting');
    let token;
    try {
      const minted = await XApi.keyService.getWsToken(endpoint._raw?.id || endpoint.id);
      token = minted && minted.token;
      if (!token) throw new Error('mint returned no token');
    } catch (e) { setStatusBoth('error'); setErrorMsg('ws-token mint 失败: ' + (e && e.message ? e.message : e)); return; }

    const url = XApi.helpers.buildWsGatewayUrl(api.host, clientPath, { endpoint: endpoint._raw?.id || endpoint.id });
    let ws;
    try { ws = new WebSocket(url, XApi.helpers.buildWsCredentialProtocols(token)); } catch (e) { setStatusBoth('error'); setErrorMsg('WebSocket 构造失败: ' + (e && e.message ? e.message : e)); return; }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    ws.onopen = () => { sendFrame(clientRef.current.startConnection()); };
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') return; // 豆包全二进制
      // 串行处理保帧序(解压是异步的)
      chainRef.current = chainRef.current.then(async () => {
        const f = await clientRef.current.parseServerFrame(ev.data);
        handleFrame(f);
      }).catch(() => {});
    };
    ws.onerror = () => setErrorMsg((m) => m || 'WebSocket error');
    ws.onclose = (ev) => {
      teardown(false); wsRef.current = null; setPhase('idle');
      if (statusRef.current !== 'error') setStatusBoth('closed');
      if (ev.code !== 1000 && ev.code !== 1005) setErrorMsg((m) => m || `Closed · code ${ev.code}${ev.reason ? ` · ${ev.reason}` : ''}`);
    };
  };

  const disconnect = () => { teardown(true); setStatusBoth('closed'); setPhase('idle'); };

  // 文本输入 → SayHello(内容作为用户提示，模型据此回复)。
  const sendText = () => {
    const v = input.trim();
    if (!v || (statusRef.current !== 'ready' && statusRef.current !== 'recording')) return;
    stopPlayback();
    setTurns((prev) => [...prev, { id: nextId(), role: 'user', text: v }]);
    sendFrame(clientRef.current.sayHello(sidRef.current, v));
    setPhase('thinking');
    setInput('');
  };

  const startMic = async () => {
    if (statusRef.current !== 'ready' || micStreamRef.current) return;
    if (!isSecure()) { setErrorMsg('麦克风需要 HTTPS 或 localhost'); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } }); }
    catch (e) { setErrorMsg('无法访问麦克风: ' + (e && e.message ? e.message : e)); return; }
    micStreamRef.current = stream;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx(); micCtxRef.current = ctx;
    try { if (ctx.state === 'suspended') await ctx.resume(); } catch {}
    const source = ctx.createMediaStreamSource(stream);
    const inAn = ctx.createAnalyser(); inAn.fftSize = 1024;
    source.connect(inAn); micAnalyserRef.current = inAn;
    const proc = ctx.createScriptProcessor(4096, 1, 1); micNodeRef.current = proc;
    proc.onaudioprocess = (e) => {
      const ws = wsRef.current, sid = sidRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || !sid) return;
      const pcm = floatToPcm16(e.inputBuffer.getChannelData(0), ctx.sampleRate);
      if (pcm.length) { try { ws.send(clientRef.current.taskRequestAudio(sid, pcm)); } catch {} }
    };
    source.connect(proc); proc.connect(ctx.destination);
    setStatusBoth('recording'); setPhase('listening');
  };

  const live = status === 'ready' || status === 'recording' || status === 'connecting';
  const ready = status === 'ready' || status === 'recording';
  const lastUser = React.useMemo(() => [...turns].reverse().find((t) => t.role === 'user'), [turns]);
  const lastAsst = React.useMemo(() => [...turns].reverse().find((t) => t.role === 'assistant'), [turns]);
  const estCost = usage.inTok * price.inAudio + usage.outAudio * price.outAudio + (usage.outTok - usage.outAudio) * price.outText;
  const dot = status === 'recording' ? 'var(--err)' : status === 'ready' ? 'var(--ok, #35c98a)' : status === 'connecting' ? 'var(--c-amber, #f0b23a)' : status === 'error' ? 'var(--err)' : 'var(--fg-4)';

  return (
    <div className="col gap-3" style={{ minWidth: 0 }}>
      {/* 连接 + 状态 + 用量 */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">Realtime 对话 · 豆包端到端实时语音 3.0</div>
          <div className="row gap-2" style={{ alignItems: 'center', fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
            <span style={{ color: 'var(--fg-3)' }}>{status}</span>
          </div>
        </div>
        <div style={{ padding: 14 }}>
          <div className="row gap-3" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {!live ? (
              <button className="btn btn-primary" onClick={connect} disabled={status === 'connecting'} style={{ height: 40, minWidth: 130 }}>
                {status === 'connecting' ? <><div className="spinner" style={{ width: 14, height: 14 }} /> 连接中…</> : <><XIcons.IconPlay size={14} /> 连接</>}
              </button>
            ) : (
              <button className="btn" onClick={disconnect} style={{ height: 40, minWidth: 130, borderColor: 'var(--err)', color: 'var(--err)' }}>
                <XIcons.IconPause size={14} /> 断开
              </button>
            )}
            <label className="row gap-2" style={{ alignItems: 'center', fontSize: 12.5, color: 'var(--fg-3)', whiteSpace: 'nowrap', flexShrink: 0 }}>
              <span style={{ whiteSpace: 'nowrap' }}>输出</span>
              <select className="input" value={modality} disabled={live} onChange={(e) => setModality(e.target.value)} style={{ height: 32, maxWidth: 120 }}>
                <option value="text">文本</option>
                <option value="audio">语音</option>
              </select>
            </label>
          </div>
          <div className="row gap-3" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
            <span>输入 token <span className="mono" style={{ color: 'var(--fg-1)' }}>{usage.inTok}</span></span>
            <span>输出 token <span className="mono" style={{ color: 'var(--fg-1)' }}>{usage.outTok}</span> <span style={{ color: 'var(--fg-4)' }}>({usage.outAudio} 音频)</span></span>
            {estCost > 0 && <span>估算 <span className="mono" style={{ color: 'var(--fg-1)' }}>${estCost.toFixed(6)}</span></span>}
          </div>
        </div>
      </div>

      {/* 会话配置 */}
      <div className="card">
        <button onClick={() => setShowCfg((v) => !v)}
          style={{ width: '100%', padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: showCfg ? '1px solid var(--line)' : 'none', color: 'var(--fg-2)' }}>
          <span className="micro">会话配置 · 人设 system_role / 说话风格 / 机器人名</span>
          <XIcons.IconChevronDown size={13} style={{ transform: showCfg ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        {showCfg && (
          <div className="col gap-3" style={{ padding: 14 }}>
            <label className="col gap-1">
              <span style={{ fontSize: 12, color: 'var(--fg-2)', fontWeight: 600 }}>人设 system_role（System Prompt）</span>
              <textarea className="input" rows={3} style={{ resize: 'vertical' }} value={systemRole} disabled={live}
                onChange={(e) => setSystemRole(e.target.value)}
                placeholder="给模型设定角色 / 背景，例如：你是一只又饿又有点笨的大灰狼，正在和迷路的小红帽对话。" />
              <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>连接前设置，会随 StartSession 的 dialog 下发（可选，留空则用默认人设）。</span>
            </label>
            <label className="col gap-1">
              <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>说话风格 speaking_style(可选)</span>
              <input className="input" value={speakingStyle} disabled={live} onChange={(e) => setSpeakingStyle(e.target.value)} placeholder="口吻气冲冲又有点可爱、语速适中…" style={{ maxWidth: 360 }} />
            </label>
            <label className="col gap-1">
              <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>机器人名 bot_name</span>
              <input className="input" value={botName} disabled={live} onChange={(e) => setBotName(e.target.value)} placeholder="豆包" style={{ maxWidth: 220 }} />
            </label>
            {live && <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>会话进行中,配置锁定;断开后可改。</div>}
          </div>
        )}
      </div>

      {errorMsg && (
        <div className="row gap-2" style={{ padding: '10px 12px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 8, fontSize: 12.5, color: 'var(--err)' }}>
          <XIcons.IconInfo size={13} /> <span style={{ overflowWrap: 'anywhere' }}>{errorMsg}</span>
        </div>
      )}

      {/* 对话 */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">对话</div>
          {turns.length > 0 && <XPrim.CopyBtn text={turns.map((t) => `${t.role}: ${t.text}`).join('\n')} />}
        </div>
        {modality === 'audio' ? (
          <div className="col" style={{ alignItems: 'center', justifyContent: 'center', gap: 16, padding: '30px 16px', minHeight: 340,
            background: 'radial-gradient(120% 80% at 50% 32%, rgba(80,120,220,0.06), transparent)' }}>
            <WsRealtimeOrb micAnalyserRef={micAnalyserRef} outAnalyserRef={outAnalyserRef} phase={live ? phase : 'idle'} size={200} />
            <div className="micro" style={{ color: 'var(--fg-3)', letterSpacing: 0.3 }}>{PHASE_LABEL[live ? phase : 'idle']}</div>
            <div className="col gap-1" style={{ alignItems: 'center', textAlign: 'center', maxWidth: 480, minHeight: 42 }}>
              {lastUser && <div style={{ fontSize: 12.5, color: 'var(--fg-4)', overflowWrap: 'anywhere' }}>{lastUser.text || '…'}</div>}
              {lastAsst && (
                <div style={{ fontSize: 15, color: 'var(--fg-1)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                  {lastAsst.text}{lastAsst.streaming && <span className="mono" style={{ color: 'var(--fg-3)' }}>▋</span>}
                </div>
              )}
              {!lastUser && !lastAsst && (
                <div style={{ fontSize: 13, color: 'var(--fg-4)' }}>{ready ? '麦克风已开启 · 直接说话即可' : '点「连接」开始 豆包实时语音 对话。'}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="col gap-2" style={{ padding: 14, minHeight: 160, maxHeight: 420, overflowY: 'auto' }}>
            {turns.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--fg-4)' }}>
                {ready ? '已连接。下方输入消息,或切到「语音」用麦克风对话。' : '点「连接」开始 豆包实时语音 对话。'}
              </div>
            ) : turns.map((t) => (
              <div key={t.id} className="row" style={{ justifyContent: t.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '82%', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', padding: '8px 12px', borderRadius: 10,
                  background: t.role === 'user' ? 'var(--accent, #5b8cff)' : 'var(--bg-3, var(--panel-2))',
                  color: t.role === 'user' ? '#fff' : 'var(--fg-1)', border: t.role === 'user' ? 'none' : '1px solid var(--line)' }}>
                  {t.text || (t.streaming ? '' : <span style={{ color: 'var(--fg-4)' }}>(无文本)</span>)}
                  {t.streaming && <span className="mono" style={{ color: 'var(--fg-3)' }}>▋</span>}
                </div>
              </div>
            ))}
          </div>
        )}
        {/* 输入区 */}
        <div className="row gap-2" style={{ padding: 12, borderTop: '1px solid var(--line)', alignItems: 'center' }}>
          <textarea className="input mono" rows={2} style={{ resize: 'none', flex: 1, minWidth: 0 }}
            placeholder={ready ? '输入消息…(Enter 发送)' : '先连接…'} value={input} disabled={!ready}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }} />
          <button className="btn btn-primary" style={{ minWidth: 78, height: 40, flexShrink: 0 }} disabled={!ready || !input.trim()} onClick={sendText}>发送</button>
        </div>
      </div>
    </div>
  );
}
